import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import type { TFunction } from "i18next";
import { Badge } from "@/components/ui/badge";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type { Filterdescriptor } from "@/components/ui/data-table-filter";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import { useFilterStand } from "@/lib/useFilterStand";
import { reistijdCell, reistijdWaarde } from "./reistijdKolom";
import { ANSWER, useInschrijvingen, type Inschrijving } from "./useDeelname";

const helper = createColumnHelper<typeof dataTableFeatures, Inschrijving>();

const kolommen = (t: TFunction<"deelname">) =>
  helper.columns([
    helper.accessor((rij) => `${rij.ploegName} ${rij.klasseName}`, {
      id: "ploeg",
      header: t("ploegen.ploeg"),
      cell: (info) => (
        <span className="block">
          <span className="font-medium">{info.row.original.ploegName}</span>
          <span className="block text-xs text-muted-foreground">
            {info.row.original.klasseName}
          </span>
        </span>
      ),
    }),
    helper.accessor("korpsName", {
      header: t("ploegen.korps"),
      // Through to the korps, where a beheerder may act namens it. This screen only reads.
      cell: (info) => (
        <Link
          to="/ploegen"
          search={{ korps: info.row.original.korpsId }}
          className="underline-offset-4 hover:underline"
        >
          {info.getValue()}
        </Link>
      ),
    }),
    helper.accessor("answer", {
      header: t("ploegen.antwoord"),
      cell: (info) => {
        const rij = info.row.original;

        if (rij.answer === ANSWER.voorkeur) {
          return (
            <Badge>{t("ploegen.voorkeur", { rang: Number(rij.rank) })}</Badge>
          );
        }

        if (rij.answer === ANSWER.geenVoorkeur) {
          return <Badge variant="destructive">{t("ploegen.lieverNiet")}</Badge>;
        }

        // An answer, not a gap: a dag nobody ticked is simply possible.
        return (
          <span className="text-sm text-muted-foreground">
            {t("ploegen.nietsGezegd")}
          </span>
        );
      },
    }),
    helper.accessor((rij) => reistijdWaarde(rij.reistijdSeconds), {
      id: "reistijd",
      header: t("gedeeld.reistijd"),
      cell: (info) => reistijdCell(info.getValue(), t),
    }),
  ]);

/** Which ploegen asked for this wedstrijddag, which ruled it out, and which said nothing. */
export function Inschrijvingen({ wedstrijddagId }: { wedstrijddagId: string }) {
  const { t } = useTranslation("deelname");
  const { data, isPending, error } = useInschrijvingen(wedstrijddagId);

  const descriptors = useMemo<Filterdescriptor<Inschrijving>[]>(
    () => [
      {
        id: "antwoord",
        label: t("ploegen.filterAntwoord"),
        kind: "meerkeuze",
        opties: [
          {
            waarde: String(ANSWER.voorkeur),
            label: t("ploegen.filterVoorkeur"),
          },
          {
            waarde: String(ANSWER.geenVoorkeur),
            label: t("ploegen.lieverNiet"),
          },
          { waarde: String(ANSWER.geen), label: t("ploegen.nietsGezegd") },
        ],
        // On before anybody touches it: who wants this dag is what the tab is opened for.
        standaard: {
          soort: "meerkeuze",
          waarden: [String(ANSWER.voorkeur), String(ANSWER.geenVoorkeur)],
        },
        past: (rij, waarde) =>
          waarde.soort !== "meerkeuze" ||
          waarde.waarden.includes(String(rij.answer)),
      },
      {
        id: "klasse",
        label: t("ploegen.filterKlasse"),
        kind: "meerkeuze",
        opties: [
          ...new Map(
            (data ?? []).map((r) => [r.klasseId, r.klasseName]),
          ).entries(),
        ].map(([waarde, label]) => ({ waarde, label })),
        past: (rij, waarde) =>
          waarde.soort !== "meerkeuze" || waarde.waarden.includes(rij.klasseId),
      },
    ],
    [data, t],
  );

  const { stand, onStand } = useFilterStand(descriptors, "/wedstrijddagen");

  if (error) {
    return <p className="text-sm text-destructive">{error.message}</p>;
  }

  if (isPending || !data) {
    return (
      <DataTableSkeleton
        label={t("ploegen.label")}
        columns={kolommen(t)}
        rows={6}
        search
        twoLine={["ploeg"]}
      />
    );
  }

  return (
    <DataTable
      label={t("ploegen.label")}
      data={data}
      columns={kolommen(t)}
      getRowId={(rij) => `${rij.ploegId}-${rij.klasseId}`}
      searchPlaceholder={t("ploegen.zoek")}
      exportHref={exportHref(
        "/api/beheer/wedstrijddagen/{wedstrijddagId}/inschrijvingen/export",
        { path: { wedstrijddagId } },
      )}
      clientPageSize={FULL_PAGE_SIZE}
      filters={{ descriptors, stand, onStand }}
    />
  );
}
